/**
 * IntegrationsTab — F18 T18.1.b (Settings → Integracje).
 *
 * Pełen CRUD UI dla integracji ERP (Symfonia w MVP):
 *   - Tabela listing: name / symfoniaCompany / URL / status / lastSync / akcje
 *   - Dialog dodawania (ErpIntegrationFormDialog mode='create')
 *   - Dialog edycji (mode='edit', credentials opcjonalne)
 *   - ConfirmDialog soft-delete (CLAUDE.md §4.12 — żaden window.confirm)
 *   - Toggle „Pokaż usunięte" + button Restore z koszem
 *
 * Akcje operacyjne (Test połączenia, Synchronizuj słowniki, Token agenta,
 * synchronizacje, mapowania, historia, dokumenty FK) żyją teraz „w środku" —
 * po kliknięciu „Otwórz" w szczegółach integracji (IntegrationDetail).
 *
 * Endpointy `/api/erp-integrations*` z T18.0 (krok 5). withRequireModule owinięty
 * od T18.1.b → 402 jeśli tenant nie ma subskrypcji symfonia_integration.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Checkbox } from '@/components/Checkbox';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Spinner } from '@/components/Spinner';
import { Tooltip } from '@/components/Tooltip';
import { type CompanyPickerOption } from '@/components/ui/CompanyPicker';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { useActiveCompanyScope } from '@/lib/use-active-company';

import { AgentStatusBadge } from './AgentStatusBadge';
import {
  DEFAULT_FORM_VALUES,
  type ErpConnectionMode,
  ErpIntegrationFormDialog,
  type ErpIntegrationFormValues,
  type ErpSyncSchedule,
  type ErpSystem,
  type ErpWebapiModule,
} from './ErpIntegrationFormDialog';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ErpIntegrationDto {
  id: string;
  companyId: string;
  name: string;
  erpSystem: string;
  connectionMode: string;
  webapiUrl: string;
  webapiVersion: string;
  deviceName?: string;
  applicationGuidSet?: boolean;
  symfoniaCompany: string;
  enabledModules: ErpWebapiModule[] | null;
  maxSessions: number;
  isActive: boolean;
  syncSchedule: ErpSyncSchedule;
  lastSyncAt: string | null;
  // T18.3 — czy token agenta został wygenerowany (klient widzi „Wygeneruj"
  // vs „Wygeneruj nowy" w UI).
  agentTokenSet: boolean;
  createdAt: string;
  updatedAt: string;
  gcrecord: string | null;
  gcrecordReason: string | null;
}

type CreatePayload = ErpIntegrationFormValues;

// PATCH payload — credentials opcjonalne, companyId NIGDY (immutable)
type UpdatePayload = Partial<Omit<ErpIntegrationFormValues, 'companyId'>>;

const INTEGRATIONS_KEY = ['erp-integrations'] as const;

function formatLastSync(value: string | null, t: (k: string) => string): string {
  if (!value) return t('tenant.settings.integrations.list.neverSynced');
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

export function IntegrationsTab({
  hideTitle = false,
  onOpen,
}: { hideTitle?: boolean; onOpen?: (id: string) => void } = {}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { me } = useTenantAuth();

  const companies = useMemo(() => me?.companies ?? [], [me?.companies]);
  // Integracja jest PER FIRMA — mapa id→nazwa, żeby pokazać firmę przy każdym wierszu listy.
  const companyNameById = useMemo(() => new Map(companies.map((c) => [c.id, c.name])), [companies]);
  // Core invariant (create bez konkretnej firmy w trybie "Wszystkie firmy" jest
  // zabronione): `useActiveCompanyScope()` — w przeciwieństwie do surowego
  // `resolveActiveCompanyId` — zwraca `companyId: null` (nie sentinel `'all'`)
  // gdy user jest w trybie "Wszystkie firmy". Bug sprzed fixu: `resolveActiveCompanyId`
  // zwracał tu literalny string sentinela ('all'), który leciał jako prefill
  // `companyId` do dialogu tworzenia integracji — POST wysyłał 'all' zamiast
  // realnego UUID firmy.
  const companyScope = useActiveCompanyScope();

  const [showDeleted, setShowDeleted] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<ErpIntegrationDto | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ErpIntegrationDto | null>(null);

  const listQ = useQuery({
    queryKey: [...INTEGRATIONS_KEY, { includeDeleted: showDeleted }],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (showDeleted) params.includeDeleted = 'true';
      const res = await api.get<{ integrations: ErpIntegrationDto[] }>('/erp-integrations', {
        params,
      });
      return res.data.integrations;
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: INTEGRATIONS_KEY });
  };

  const createMutation = useMutation({
    mutationFn: async (values: CreatePayload) => {
      const res = await api.post<{ integration: ErpIntegrationDto }>('/erp-integrations', {
        companyId: values.companyId,
        name: values.name,
        erpSystem: values.erpSystem,
        connectionMode: values.connectionMode,
        webapiUrl: values.webapiUrl || undefined,
        webapiVersion: values.webapiVersion || undefined,
        applicationGuid: values.applicationGuid,
        deviceName: values.deviceName,
        symfoniaCompany: values.symfoniaCompany,
        enabledModules: values.enabledModules,
        maxSessions: values.maxSessions,
        isActive: values.isActive,
        syncSchedule: values.syncSchedule,
      });
      return res.data.integration;
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('tenant.settings.integrations.toast.created') });
      setCreateOpen(false);
    },
    onError: (err) => {
      const code = isAxiosError(err) ? (err.response?.data?.error as string | undefined) : null;
      if (code === 'INTEGRATION_NAME_TAKEN') {
        toast.show({
          variant: 'warning',
          title: t('tenant.settings.integrations.toast.nameTaken'),
        });
        return;
      }
      toast.show({ variant: 'error', title: t('tenant.settings.integrations.toast.createError') });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, values }: { id: string; values: ErpIntegrationFormValues }) => {
      // PATCH: pomiń companyId (immutable) i puste credentials (zachowaj stare).
      const payload: UpdatePayload = {
        name: values.name,
        erpSystem: values.erpSystem,
        connectionMode: values.connectionMode,
        webapiUrl: values.webapiUrl || undefined,
        webapiVersion: values.webapiVersion || undefined,
        symfoniaCompany: values.symfoniaCompany,
        enabledModules: values.enabledModules,
        maxSessions: values.maxSessions,
        isActive: values.isActive,
        syncSchedule: values.syncSchedule,
        deviceName: values.deviceName,
      };
      // applicationGuid: tylko gdy user wpisał (puste = zachowaj obecne, handler pomija).
      if (values.applicationGuid) payload.applicationGuid = values.applicationGuid;
      const res = await api.patch<{ integration: ErpIntegrationDto }>(
        `/erp-integrations/${id}`,
        payload,
      );
      return res.data.integration;
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('tenant.settings.integrations.toast.updated') });
      setEditing(null);
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('tenant.settings.integrations.toast.updateError') });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/erp-integrations/${id}`);
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('tenant.settings.integrations.toast.deleted') });
      setPendingDelete(null);
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('tenant.settings.integrations.toast.deleteError') });
      setPendingDelete(null);
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/erp-integrations/${id}/restore`);
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('tenant.settings.integrations.toast.restored') });
    },
    onError: (err) => {
      const code = isAxiosError(err) ? (err.response?.data?.error as string | undefined) : null;
      if (code === 'INTEGRATION_NAME_CONFLICT') {
        toast.show({
          variant: 'warning',
          title: t('tenant.settings.integrations.toast.nameConflict'),
        });
        return;
      }
      toast.show({ variant: 'error', title: t('tenant.settings.integrations.toast.restoreError') });
    },
  });

  const integrations = listQ.data ?? [];
  // Przycisk „Dodaj integrację" wymaga TYLKO że jest z czego wybrać firmę —
  // w trybie "Wszystkie firmy" dialog nadal się otwiera, ale pole firmy startuje
  // puste i wymaga jawnego wyboru (walidacja w ErpIntegrationFormDialog).
  const canCreateIntegration = companies.length > 0;
  // CompanyPicker oczekuje `{id, legalName}` — TenantCompany ma `.name`.
  const companyPickerOptions: CompanyPickerOption[] = useMemo(
    () => companies.map((c) => ({ id: c.id, legalName: c.name })),
    [companies],
  );

  const createInitialValues: ErpIntegrationFormValues = useMemo(
    () => ({
      ...DEFAULT_FORM_VALUES,
      // Prefill TYLKO w trybie konkretnej firmy — w trybie "Wszystkie firmy"
      // (companyScope.mode === 'all') albo bez żadnej firmy ('none') pole
      // zostaje puste i user MUSI wybrać w dialogu (core invariant — brak
      // create bez jawnie wybranej konkretnej firmy).
      companyId: companyScope.mode === 'company' ? (companyScope.companyId ?? '') : '',
    }),
    [companyScope.mode, companyScope.companyId],
  );

  const editInitialValues: ErpIntegrationFormValues | null = editing
    ? {
        companyId: editing.companyId,
        name: editing.name,
        erpSystem: (editing.erpSystem as ErpSystem) ?? 'symfonia_handel',
        connectionMode: (editing.connectionMode as ErpConnectionMode) ?? 'agent',
        webapiUrl: editing.webapiUrl,
        webapiVersion: editing.webapiVersion,
        // applicationGuid puste w UI (placeholder „nie zmieniaj") — handler go nie ruszy
        // jeśli zostanie puste przy submit. deviceName jawny — prefill z DTO.
        applicationGuid: '',
        deviceName: editing.deviceName ?? 'Workflow365',
        symfoniaCompany: editing.symfoniaCompany,
        enabledModules: editing.enabledModules ?? [],
        maxSessions: editing.maxSessions,
        syncSchedule: editing.syncSchedule,
        isActive: editing.isActive,
      }
    : null;

  return (
    <div data-testid="integrations-tab">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          {!hideTitle && (
            <>
              <h2 className="text-lg font-semibold text-text-primary">
                {t('tenant.settings.integrations.title')}
              </h2>
              <p className="text-xs text-text-tertiary">
                {t('tenant.settings.integrations.subtitle')}
              </p>
            </>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Checkbox
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
            label={t('tenant.settings.integrations.showDeleted')}
            data-testid="integrations-show-deleted"
          />
          <Button
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => setCreateOpen(true)}
            disabled={!canCreateIntegration}
            data-testid="integrations-new-btn"
          >
            {t('tenant.settings.integrations.newIntegration')}
          </Button>
        </div>
      </header>

      {listQ.isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Spinner />
        </div>
      ) : integrations.length === 0 ? (
        <Card padding="md">
          <p className="text-sm text-text-tertiary">
            {showDeleted
              ? t('tenant.settings.integrations.list.emptyDeleted')
              : t('tenant.settings.integrations.list.empty')}
          </p>
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border-primary bg-bg-secondary text-xs uppercase tracking-wider text-text-muted">
                <tr>
                  <th className="px-4 py-2 text-left">
                    {t('tenant.settings.integrations.list.col.name')}
                  </th>
                  <th className="px-4 py-2 text-left">
                    {t('tenant.settings.integrations.list.col.symfoniaCompany')}
                  </th>
                  <th className="px-4 py-2 text-left">
                    {t('tenant.settings.integrations.list.col.url')}
                  </th>
                  <th className="px-4 py-2 text-left">
                    {t('tenant.settings.integrations.list.col.status')}
                  </th>
                  <th className="px-4 py-2 text-left">
                    {t('tenant.settings.integrations.list.col.lastSync')}
                  </th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-primary">
                {integrations.map((it) => {
                  const isDeleted = it.gcrecord !== null;
                  return (
                    <tr
                      key={it.id}
                      data-testid={`integration-row-${it.id}`}
                      className={isDeleted ? 'opacity-60' : ''}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="font-medium text-text-primary">{it.name}</div>
                            {companyNameById.get(it.companyId) && (
                              <div
                                className="mt-0.5 text-xs text-text-tertiary"
                                data-testid={`integration-company-${it.id}`}
                              >
                                {companyNameById.get(it.companyId)}
                              </div>
                            )}
                            <div className="mt-0.5 flex flex-wrap items-center gap-1">
                              <Badge
                                color="blue"
                                size="sm"
                                data-testid={`integration-system-badge-${it.id}`}
                              >
                                {t(`tenant.settings.integrations.form.erpSystems.${it.erpSystem}`)}
                              </Badge>
                              <Badge
                                color="gray"
                                size="sm"
                                data-testid={`integration-mode-badge-${it.id}`}
                              >
                                {t(
                                  `tenant.settings.integrations.list.modeShort.${it.connectionMode}`,
                                )}
                              </Badge>
                            </div>
                            {/* Status agenta ma sens TYLKO w trybie agentowym — w trybie
                                bezpośrednim agenta nie ma, więc „Nie podpięto" myliło. */}
                            {!isDeleted && it.connectionMode === 'agent' && (
                              <AgentStatusBadge integrationId={it.id} />
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-text-secondary">{it.symfoniaCompany}</td>
                      <td className="px-4 py-3 text-text-tertiary">
                        <code className="rounded bg-bg-secondary px-1.5 py-0.5 text-xs">
                          {it.webapiUrl}
                        </code>
                      </td>
                      <td className="px-4 py-3">
                        {isDeleted ? (
                          <Badge color="gray">
                            {t('tenant.settings.integrations.list.statusDeleted')}
                          </Badge>
                        ) : it.isActive ? (
                          <Badge color="accent">
                            {t('tenant.settings.integrations.list.statusActive')}
                          </Badge>
                        ) : (
                          <Badge color="gray">
                            {t('tenant.settings.integrations.list.statusInactive')}
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-text-tertiary">
                        {formatLastSync(it.lastSyncAt, t)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {isDeleted ? (
                            <Tooltip content={t('tenant.settings.integrations.list.actionRestore')}>
                              <button
                                type="button"
                                onClick={() => restoreMutation.mutate(it.id)}
                                aria-label={t('tenant.settings.integrations.list.actionRestore')}
                                className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary"
                                data-testid={`integration-restore-${it.id}`}
                              >
                                <RotateCcw className="h-3.5 w-3.5" />
                              </button>
                            </Tooltip>
                          ) : (
                            <>
                              {onOpen && (
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => onOpen(it.id)}
                                  data-testid={`integration-open-${it.id}`}
                                >
                                  {t('tenant.settings.integrations.list.actionOpen')}
                                </Button>
                              )}
                              <Tooltip content={t('tenant.settings.integrations.list.actionEdit')}>
                                <button
                                  type="button"
                                  onClick={() => setEditing(it)}
                                  aria-label={t('tenant.settings.integrations.list.actionEdit')}
                                  className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary"
                                  data-testid={`integration-edit-${it.id}`}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                              </Tooltip>
                              <Tooltip
                                content={t('tenant.settings.integrations.list.actionDelete')}
                              >
                                <button
                                  type="button"
                                  onClick={() => setPendingDelete(it)}
                                  aria-label={t('tenant.settings.integrations.list.actionDelete')}
                                  className="rounded p-1.5 text-text-muted transition hover:bg-error-500/10 hover:text-error-500"
                                  data-testid={`integration-delete-${it.id}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </Tooltip>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <ErpIntegrationFormDialog
        open={createOpen}
        mode="create"
        companies={companyPickerOptions}
        initialValues={createInitialValues}
        saving={createMutation.isPending}
        onSubmit={(values) => createMutation.mutate(values)}
        onClose={() => setCreateOpen(false)}
      />

      {editInitialValues && (
        <ErpIntegrationFormDialog
          open={editing !== null}
          mode="edit"
          companies={companyPickerOptions}
          initialValues={editInitialValues}
          saving={updateMutation.isPending}
          onSubmit={(values) => {
            if (editing) updateMutation.mutate({ id: editing.id, values });
          }}
          onClose={() => setEditing(null)}
        />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('tenant.settings.integrations.confirmDelete.title')}
        message={
          pendingDelete
            ? t('tenant.settings.integrations.confirmDelete.message', {
                name: pendingDelete.name,
              })
            : ''
        }
        confirmLabel={t('tenant.settings.integrations.list.actionDelete')}
        variant="danger"
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
        }}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}
